import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "../../components/SEO";

// (No configuratorData needed, removed)

const valuesTwo = [
  {
    icon: "🎨",
    title: "Pantone Color Matching",
    description:
      "We maintain brand consistency across all print cycles using advanced Pantone matching and color density checks, ensuring repeat runs look identical.",
  },
  {
    icon: "🧪",
    title: "Adhesive Engineering",
    description:
      "We match the ideal substrate (BOPP, Chromo, PET) with the perfect adhesive (permanent, removable, or freezer-grade) based on your product container and environment.",
  },
  {
    icon: "⚙️",
    title: "Applicator Compatibility",
    description:
      "Our labels are finished and wound on rolls with precise core tension, direction, and gap spacing to ensure seamless, jam-free runs on automatic labeling machines.",
  },
  {
    icon: "🔎",
    title: "Multi-Stage Inspection",
    description:
      "Every label roll undergoes rigorous inspection to eliminate print smudges, registration offsets, and die-cut errors before shipping.",
  },
];

const mapPoints = [
  {
    id: "surat",
    name: "Surat, Gujarat (HQ & Factory)",
    x: 530,
    y: 260,
    type: "hq",
    market: "Central Production Hub",
    details: "Our 100% in-house manufacturing plant. Equipped with 8-color servo flexo lines and digital presses. Coordinates all domestic and export logistics.",
    stats: "24/7 Production Capacity"
  },
  {
    id: "usa",
    name: "North America (USA)",
    x: 180,
    y: 160,
    type: "export",
    market: "Export Market",
    details: "Supplying FDA-compliant labels for cosmetics, nutraceuticals, and organic food brands. Built with premium materials to meet US customs and shelf requirements.",
    stats: "FDA-Compliant Standards"
  },
  {
    id: "europe",
    name: "United Kingdom & Europe",
    x: 420,
    y: 130,
    type: "export",
    market: "Export Market",
    details: "Delivering sustainable retail labels, luxury hot-foil embellishments, and textured wine bottle stickers for boutique European brands.",
    stats: "Eco-Friendly Material Options"
  },
  {
    id: "uae",
    name: "Middle East (Dubai/UAE)",
    x: 460,
    y: 220,
    type: "export",
    market: "Export Market",
    details: "Supplying pharmaceutical labels, serialised barcodes, and high-volume retail product packaging to fast-growing GCC trade sectors.",
    stats: "Express Middle East Logistics"
  },
  {
    id: "sea",
    name: "Southeast Asia (Singapore/Vietnam)",
    x: 690,
    y: 300,
    type: "export",
    market: "Export Market",
    details: "Providing durable electronic warning decals, logistics barcode rolls, and weather-resistant industrial labels for manufacturing zones.",
    stats: "Chemical & Heat-Resistant Substrates"
  },
  {
    id: "africa",
    name: "East Africa",
    x: 480,
    y: 360,
    type: "export",
    market: "Export Market",
    details: "Supplying agricultural chemical drum labels, FMCG product stickers, and food packaging tags with strong rub resistance.",
    stats: "Durable Shipping Coordination"
  },
  {
    id: "delhi",
    name: "North India (NCR & Punjab)",
    x: 535,
    y: 200,
    type: "domestic",
    market: "Domestic Market",
    details: "Daily cargo dispatches to pharmaceutical plants, consumer products clusters, and major retail warehouses in Delhi-NCR, Punjab, and Haryana.",
    stats: "2-3 Days Turnaround"
  },
  {
    id: "bengaluru",
    name: "South India (BLR, CHN, HYD)",
    x: 528,
    y: 320,
    type: "domestic",
    market: "Domestic Market",
    details: "Serving leading tech hardware brands, automotive parts suppliers, and high-end organic cosmetic startups across Karnataka, Tamil Nadu, and Telangana.",
    stats: "Express Door Delivery"
  },
  {
    id: "kolkata",
    name: "East India (Kolkata & Assam)",
    x: 590,
    y: 245,
    type: "domestic",
    market: "Domestic Market",
    details: "Supplying retail stickers, price tags, and moisture-resistant tea packaging labels to corporate and estate sectors in West Bengal and Northeast states.",
    stats: "Custom Winding for Packagers"
  }
];

const AboutUs = () => {
  const [hoveredPoint, setHoveredPoint] = useState(mapPoints[0]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO
        title="About Us | Best Label Printing Company in Surat"
        description="Learn about Sahajink Pvt Ltd, a leading ISO-certified label manufacturer in Surat, Gujarat. With 4+ years of experience in premium custom label printing for global brands."
        keywords="about sahajink, label manufacturer surat, printing press gujarat, custom label experts india, industrial labels about"
      />
      <main className="min-h-screen overflow-x-hidden bg-[#0a1628] text-white">
        {/* Hero Section */}
        <section className="relative overflow-hidden pt-[140px] pb-20 md:pt-[180px] md:pb-24 lg:pt-[210px] lg:pb-28">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.2),transparent_40%),radial-gradient(circle_at_15%_80%,rgba(96,165,250,0.1),transparent_30%),linear-gradient(180deg,#0a1628_0%,#091120_100%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px] opacity-40" />

          <div className="relative z-10 mx-auto max-w-[1200px] px-6 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
            >
              <div className="mb-8 inline-flex items-center gap-3 rounded-full border border-blue-400/20 bg-blue-500/5 px-6 py-2.5 text-[9px] sm:text-xs font-bold uppercase tracking-[0.3em] text-blue-300 backdrop-blur-xl">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                </span>
                Est. 2022 • Manufacturing Excellence
              </div>

              <h1 className="mb-8 text-[30px] sm:text-[36px] font-black leading-[1.1] text-white md:text-[62px] lg:text-[72px] tracking-tight">
                Premium Custom Labels <br />
                <span className="bg-gradient-to-r from-blue-300 via-blue-400 to-cyan-300 bg-clip-text text-transparent">
                  That Make Your Products Stand Out.
                </span>
              </h1>

              <p className="max-w-[900px] mx-auto text-[14px] sm:text-lg text-blue-100/60 md:text-xl md:leading-8 font-medium">
                At Sahajink, we specialize in high-quality self-adhesive roll labels, product stickers,
                and custom packaging solutions. Combining advanced printing technology with over 4 years of
                hands-on process expertise, we help businesses across India showcase their products with
                durable, professional, and eye-catching labels.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Story Section */}
        <section className="relative bg-white py-20 sm:py-24 text-slate-900 overflow-hidden">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-50 rounded-full blur-[120px] -mr-64 -mt-64 opacity-50"></div>

          <div className="mx-auto max-w-[1200px] px-6 relative z-10">
            <div className="grid gap-16 lg:grid-cols-2 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <div className="mb-4 inline-flex rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.25em] text-blue-700">
                  Our Legacy
                </div>
                <h2 className="mb-8 text-4xl font-extrabold tracking-tight md:text-5xl leading-tight">
                  From a Vision to <br />
                  <span className="text-blue-600">Industrial Scale.</span>
                </h2>
                <div className="space-y-4 sm:space-y-6 text-[14px] sm:text-lg leading-relaxed text-slate-600">
                  <p>
                    Founded on the principles of technical discipline and
                    commercial integrity, Sahajink began as a specialized
                    printing unit focused on high-accuracy industrial labels.
                  </p>
                  <p>
                    Today, we operate a state-of-the-art manufacturing facility serving FMCG, Pharmaceutical, Automotive, and Luxury sectors across India. Our journey has been defined by continuous technology upgrades, investment in modern printing systems, and a deep understanding of adhesive chemistry, enabling us to deliver high-quality and precision-driven label solutions.

                  </p>

                  <blockquote className="border-l-4 sm:border-l-4 border-blue-600 pl-5 text-slate-600 text-[16px] sm:text-[19px]">
                    "Our goal was never to be the biggest, but to be the most
                    precise. In industrial labeling, a failure of 1% is a
                    failure of 100%."
                  </blockquote>
                </div>

                <div className="mt-10 grid grid-cols-2 gap-8">
                  <div>
                    <div className="text-4xl font-black text-blue-600">4+</div>
                    <div className="text-sm font-bold uppercase tracking-wider text-slate-400 mt-1">
                      Years of Expertise
                    </div>
                  </div>
                  <div>
                    <div className="text-4xl font-black text-blue-600">
                      500M+
                    </div>
                    <div className="text-sm font-bold uppercase tracking-wider text-slate-400 mt-1">
                      Labels Printed
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="w-full flex flex-col justify-between bg-slate-50 border border-slate-200/80 rounded-[40px] p-6 sm:p-8 shadow-inner relative overflow-hidden">
                  <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.015)_1px,transparent_1px)] bg-[size:24px_24px] opacity-60 pointer-events-none" />
                  
                  <div className="mb-6">
                    <span className="text-xs font-bold uppercase tracking-widest text-blue-600 block mb-1">Anatomy of a Roll Label</span>
                    <h4 className="text-lg font-extrabold text-slate-800">Engineered Multi-Layer Label Structure</h4>
                  </div>

                  <div className="space-y-4 relative z-10">
                    {[
                      {
                        step: "5",
                        title: "Protective Finish Layer",
                        desc: "UV Gloss varnish, anti-scratch Matte lamination, or Spot UV highlight coat.",
                        color: "border-blue-100 bg-white hover:border-blue-300",
                        iconBg: "bg-blue-100 text-blue-600"
                      },
                      {
                        step: "4",
                        title: "8-Color Print & Hot/Cold Foil",
                        desc: "High-density UV flexo inks paired with metallic foil accents for luxury retail styling.",
                        color: "border-cyan-100 bg-white hover:border-cyan-300",
                        iconBg: "bg-cyan-100 text-cyan-600"
                      },
                      {
                        step: "3",
                        title: "Face Stock Substrate",
                        desc: "Chromo paper, metallic foil paper, or durable waterproof films (BOPP, PE, PET).",
                        color: "border-purple-100 bg-white hover:border-purple-300",
                        iconBg: "bg-purple-100 text-purple-600"
                      },
                      {
                        step: "2",
                        title: "Application-Matched Adhesive",
                        desc: "High-bond acrylics, deep freezer-grade gum, or low-migration pharmaceutical adhesive.",
                        color: "border-emerald-100 bg-white hover:border-emerald-300",
                        iconBg: "bg-emerald-100 text-emerald-600"
                      },
                      {
                        step: "1",
                        title: "Release Carrier Liner",
                        desc: "Glassine paper or PET liner calibrated for high-speed automatic applicator machines.",
                        color: "border-amber-100 bg-white hover:border-amber-300",
                        iconBg: "bg-amber-100 text-amber-600"
                      }
                    ].map((layer) => (
                      <div 
                        key={layer.step}
                        className={`p-4 rounded-2xl border ${layer.color} shadow-sm transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 flex items-start gap-4 cursor-default`}
                      >
                        <div className={`h-8 w-8 rounded-lg ${layer.iconBg} flex items-center justify-center text-xs font-black shrink-0`}>
                          {layer.step}
                        </div>
                        <div>
                          <h5 className="text-xs sm:text-sm font-bold text-slate-800">{layer.title}</h5>
                          <p className="text-[11px] text-slate-500 leading-normal mt-0.5">{layer.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Geographic Footprint Section */}
        <section className="relative py-20 sm:py-24 bg-[#0a1628] overflow-hidden">
          {/* Style block for animated dash array supply lines */}
          <style>{`
            @keyframes supplyFlow {
              from {
                stroke-dashoffset: 30;
              }
              to {
                stroke-dashoffset: 0;
              }
            }
            .supply-line-anim {
              stroke-dasharray: 6, 4;
              animation: supplyFlow 1.2s linear infinite;
            }
            @keyframes mapPulse {
              0% {
                transform: scale(0.95);
                opacity: 0.8;
              }
              50% {
                transform: scale(1.15);
                opacity: 0.4;
              }
              100% {
                transform: scale(0.95);
                opacity: 0.8;
              }
            }
            .pulse-circle {
              transform-origin: center;
              animation: mapPulse 2s ease-in-out infinite;
            }
          `}</style>

          {/* Background effects */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(37,99,235,0.08),transparent_50%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.01)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:40px_40px] opacity-20" />

          <div className="mx-auto max-w-[1200px] px-6 relative z-10">
            <div className="grid gap-12 lg:grid-cols-12 lg:items-center">

              {/* Left Column: Stats and Hover Card */}
              <div className="lg:col-span-5 space-y-8">
                <div>
                  <div className="mb-4 inline-flex rounded-full border border-blue-400/20 bg-blue-500/5 px-4 py-2 text-xs font-bold uppercase tracking-[0.25em] text-blue-300">
                    Our Supply Network
                  </div>
                  <h2 className="text-3xl font-extrabold tracking-tight text-white md:text-5xl leading-tight">
                    Delivering Quality <br />
                    <span className="bg-gradient-to-r from-blue-300 via-blue-400 to-cyan-300 bg-clip-text text-transparent">
                      Across India & Globally.
                    </span>
                  </h2>
                  <p className="mt-4 text-white/60 text-sm sm:text-base leading-relaxed">
                    From our production headquarters in Surat, Gujarat, we maintain reliable supply chains delivering custom labels to 20+ states in India and exporting to overseas markets.
                  </p>
                </div>

                {/* Live Node Info Panel */}
                <div className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur-xl shadow-2xl transition-all duration-300 hover:border-blue-400/30">
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`h-2.5 w-2.5 rounded-full ${hoveredPoint.type === 'hq' ? 'bg-red-500 animate-pulse' : hoveredPoint.type === 'export' ? 'bg-blue-400' : 'bg-emerald-400'}`} />
                    <span className="text-xs font-black uppercase tracking-widest text-blue-300">{hoveredPoint.market}</span>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-2">{hoveredPoint.name}</h3>
                  <p className="text-xs sm:text-sm text-white/60 leading-relaxed mb-4">{hoveredPoint.details}</p>

                  <div className="border-t border-white/10 pt-4 flex justify-between items-center text-[10px] sm:text-xs font-bold uppercase tracking-wider text-blue-300">
                    <span>Key strength:</span>
                    <span>{hoveredPoint.stats}</span>
                  </div>
                </div>

                {/* Small Stats Grid */}
                <div className="grid grid-cols-3 gap-4 border-t border-white/10 pt-8">
                  <div>
                    <div className="text-2xl font-black text-blue-400">20+</div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-white/40 mt-1">States Served</div>
                  </div>
                  <div>
                    <div className="text-2xl font-black text-blue-400">5+</div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-white/40 mt-1">Global Countries</div>
                  </div>
                  <div>
                    <div className="text-2xl font-black text-blue-400">100%</div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-white/40 mt-1">Traceable Transit</div>
                  </div>
                </div>
              </div>

              {/* Right Column: Interactive Styled Map */}
              <div className="lg:col-span-7 flex flex-col justify-center">
                <div className="relative w-full aspect-[2/1.2] rounded-[36px] border border-white/10 bg-white/5 p-6 backdrop-blur-xl shadow-2xl overflow-hidden group">
                  {/* Radar Circles behind Surat HQ */}
                  <svg className="absolute inset-0 w-full h-full pointer-events-none">
                    <circle cx="530" cy="260" r="100" stroke="rgba(37,99,235,0.03)" strokeWidth="1" fill="none" />
                    <circle cx="530" cy="260" r="200" stroke="rgba(37,99,235,0.03)" strokeWidth="1" fill="none" />
                    <circle cx="530" cy="260" r="300" stroke="rgba(37,99,235,0.03)" strokeWidth="1" fill="none" />
                    <circle cx="530" cy="260" r="420" stroke="rgba(37,99,235,0.02)" strokeWidth="1" fill="none" />
                  </svg>

                  {/* Interactive Map SVG */}
                  <svg viewBox="0 0 1000 500" className="w-full h-full relative z-10">

                    {/* Connection Lines from Surat HQ to Destination points */}
                    {mapPoints.map((pt) => {
                      if (pt.id === "surat") return null;

                      // Draw quadratic curve from Surat (530, 260) to point
                      const isDomestic = pt.type === "domestic";
                      const strokeColor = isDomestic ? "rgba(52, 211, 153, 0.4)" : "rgba(96, 165, 250, 0.45)";
                      const activeColor = isDomestic ? "rgba(52, 211, 153, 0.8)" : "rgba(96, 165, 250, 0.9)";

                      const isHovered = hoveredPoint.id === pt.id;

                      // Intermediate control point for curved path
                      const cx = (530 + pt.x) / 2;
                      const cy = (260 + pt.y) / 2 - (isDomestic ? 20 : 60);

                      return (
                        <g key={`path-${pt.id}`}>
                          {/* Base Glow Line */}
                          <path
                            d={`M 530 260 Q ${cx} ${cy} ${pt.x} ${pt.y}`}
                            stroke={isHovered ? activeColor : strokeColor}
                            strokeWidth={isHovered ? "2.5" : "1.5"}
                            fill="none"
                            className="transition-all duration-300"
                          />
                          {/* Flowing Dash Line */}
                          <path
                            d={`M 530 260 Q ${cx} ${cy} ${pt.x} ${pt.y}`}
                            stroke={isHovered ? activeColor : isDomestic ? "rgb(52, 211, 153)" : "rgb(96, 165, 250)"}
                            strokeWidth={isHovered ? "2.5" : "1"}
                            fill="none"
                            className="supply-line-anim transition-all duration-300"
                            style={{ opacity: isHovered ? 1 : 0.7 }}
                          />
                        </g>
                      );
                    })}

                    {/* Background abstract region labels */}
                    <text x="140" y="100" fill="rgba(255,255,255,0.08)" fontSize="10" fontWeight="bold" letterSpacing="0.4em">NORTH AMERICA</text>
                    <text x="360" y="80" fill="rgba(255,255,255,0.08)" fontSize="10" fontWeight="bold" letterSpacing="0.4em">EUROPE</text>
                    <text x="380" y="270" fill="rgba(255,255,255,0.06)" fontSize="10" fontWeight="bold" letterSpacing="0.4em">AFRICA</text>
                    <text x="690" y="240" fill="rgba(255,255,255,0.08)" fontSize="10" fontWeight="bold" letterSpacing="0.4em">SOUTHEAST ASIA</text>

                    {/* Nodes (HQ and Markets) */}
                    {mapPoints.map((pt) => {
                      const isHq = pt.type === "hq";
                      const isDomestic = pt.type === "domestic";
                      const isHovered = hoveredPoint.id === pt.id;

                      const mainColor = isHq ? "rgb(239, 68, 68)" : isDomestic ? "rgb(52, 211, 153)" : "rgb(96, 165, 250)";
                      const pulseColor = isHq ? "rgba(239, 68, 68, 0.4)" : isDomestic ? "rgba(52, 211, 153, 0.3)" : "rgba(96, 165, 250, 0.35)";

                      return (
                        <g
                          key={pt.id}
                          className="cursor-pointer"
                          onMouseEnter={() => setHoveredPoint(pt)}
                        >
                          {/* Pulsing ring */}
                          <circle
                            cx={pt.x}
                            cy={pt.y}
                            r={isHq ? "14" : isHovered ? "12" : "8"}
                            fill={pulseColor}
                            className={isHovered || isHq ? "pulse-circle" : ""}
                          />
                          {/* Inner solid point */}
                          <circle
                            cx={pt.x}
                            cy={pt.y}
                            r={isHq ? "6" : "4.5"}
                            fill={mainColor}
                            className="transition-all duration-300"
                          />

                          {/* Node label */}
                          <text
                            x={pt.x}
                            y={pt.y - (isHq ? 20 : 12)}
                            textAnchor="middle"
                            fill={isHovered ? "#fff" : "rgba(255, 255, 255, 0.65)"}
                            fontSize={isHq ? "11" : "9"}
                            fontWeight="bold"
                            className="transition-all duration-300 pointer-events-none drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]"
                          >
                            {isHq ? "Surat Factory" : pt.id === "usa" ? "USA" : pt.id === "europe" ? "UK & Europe" : pt.id === "uae" ? "UAE" : pt.id === "sea" ? "Southeast Asia" : pt.id === "africa" ? "East Africa" : pt.id === "delhi" ? "NCR" : pt.id === "bengaluru" ? "South India" : "East India"}
                          </text>
                        </g>
                      );
                    })}

                  </svg>

                  {/* Subtle map prompt */}
                  <div className="absolute bottom-4 right-6 text-[10px] font-bold text-white/30 tracking-wider flex items-center gap-2 pointer-events-none">
                    <span>💡 Hover over the dots to explore our hubs</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        <section className="relative py-24 sm:py-32 overflow-hidden bg-white border-y border-slate-100">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-50 rounded-full blur-[120px] -mr-48 -mt-48 opacity-40 pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-cyan-50 rounded-full blur-[100px] -ml-32 -mb-32 opacity-30 pointer-events-none"></div>

          <div className="mx-auto max-w-[1200px] px-6 relative z-10">
            <div className="mb-16 md:mb-20">
              <div className="mb-4 inline-flex rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.25em] text-blue-700">
                Production Standards
              </div>
              <h2 className="text-4xl font-extrabold tracking-tight text-slate-900 md:text-5xl lg:text-6xl mb-6">
                Consistent Labels, <br />
                <span className="text-blue-600 italic">Every Time</span>
              </h2>
              <p className="text-slate-600 max-w-[1000px] text-[16px] sm:text-lg font-medium leading-relaxed">
                We ensure every label stays perfectly consistent using precise materials, strong adhesives, and accurate printing, so your brand looks the same in every production batch and on every shelf.

              </p>
            </div>

            <div className="grid gap-10 md:gap-12 md:grid-cols-2">
              {valuesTwo.map((value, idx) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  className="flex flex-col sm:flex-row gap-6 sm:gap-8 group"
                >
                  <div className="shrink-0 w-16 h-16 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center text-3xl group-hover:bg-blue-600 group-hover:text-white transition-all duration-500 shadow-sm group-hover:rotate-6">
                    {value.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors">
                      {value.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed text-[15px] sm:text-lg font-medium max-w-[440px]">
                      {value.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-20 bg-blue-600 overflow-hidden">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:30px_30px] opacity-20" />
          <div className="mx-auto max-w-[1000px] px-6 text-center relative z-10">
            <h2 className="text-3xl font-extrabold text-white md:text-5xl mb-8 leading-tight">
              Ready to give your brand the <br />
              Sahajink Advantage?
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/#contact"
                className="rounded-full bg-white px-8 py-4 text-sm font-bold uppercase tracking-[0.18em] text-blue-600 shadow-xl hover:scale-105 transition duration-300 hover:bg-blue-50"
              >
                Start Your Project
              </Link>
              <Link
                to="/why-choose-us"
                className="rounded-full border border-white/30 bg-white/10 px-8 py-4 text-sm font-bold uppercase tracking-[0.18em] text-white hover:bg-white/20 transition duration-300"
              >
                Why Choose Us
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default AboutUs;
